/**
 * Squish Deli: collections, plates landing, search and 404 helpers (COLLECTIONS + LANDING role).
 *
 *   [data-sdl-when="pre|maybe|late|after"]   Christmas lines (snippets/sd-lp-christmas.liquid)
 *   [data-sdl-until="YYYY-MM-DD"] / [data-sdl-from]  inclusive New York date gates (opening offer etc.)
 *   [data-sd-menu]        feel filter on the menu grid (sections/sd-menu-grid.liquid)
 *   [data-sd-tray]        sticky "Mix any 8 for $88" plate tray (snippets/sd-lp-plate-tray.liquid; data-lead holds
 *                         "Any # from the menu for" (# = size) on counters that list the Mini)
 *   [data-sdl-plates]     plates landing page: presets, one-tap platter add, sticky buy bar
 *   video[data-sdl-inview] muted loops that only play while on screen (poster only under reduced motion)
 *
 * Liquid renders every first paint; this file only corrects cached dates and adds behaviour.
 * QA: ?sd_date=2026-11-24 pretends today is that New York date (via sd-ui nyToday()).
 */
import { addItems, getCart, openCartDrawer, nyToday, formatMoney, squish } from '@theme/sd-ui';
import { StandardEvents } from '@shopify/events';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const todayYmd = () => nyToday().toISOString().slice(0, 10);

/** BUILD-SPEC 5 phases: pre (to Nov 22), maybe (Nov 23-24), late (Nov 25-Dec 25), after (Dec 26+). */
export function christmasPhase(t = todayYmd()) {
  if (t >= '2026-12-26') return 'after';
  if (t >= '2026-11-25') return 'late';
  if (t >= '2026-11-23') return 'maybe';
  return 'pre';
}

/* ------------------------------------------------------------------ */
/* 1. Date gates                                                        */
/* ------------------------------------------------------------------ */
export function applyGates(root = document) {
  const t = todayYmd();
  const phase = christmasPhase(t);
  for (const el of root.querySelectorAll('[data-sdl-when]')) {
    el.hidden = !(el.getAttribute('data-sdl-when') || '').split(/\s+/).includes(phase);
  }
  for (const el of root.querySelectorAll('[data-sdl-until], [data-sdl-from]')) {
    const until = el.getAttribute('data-sdl-until');
    const from = el.getAttribute('data-sdl-from');
    const show = !(until && t > until) && !(from && t < from);
    el.hidden = !show;
    if (!show) for (const box of el.querySelectorAll('input[type="checkbox"]')) box.checked = false;
  }
}

/* ------------------------------------------------------------------ */
/* 2. Feel filter (menu grid)                                           */
/* ------------------------------------------------------------------ */
function initMenu(root) {
  if (root.dataset.sdlReady) return;
  root.dataset.sdlReady = 'true';
  // Server-filtered tag URLs (/collections/menu/slow-rise) only hold the matching products: let links navigate.
  if (root.hasAttribute('data-sd-server-filtered')) return;

  const chips = [...root.querySelectorAll('[data-sd-feel]')];
  const items = [...root.querySelectorAll('[data-sd-item]')];
  const shelves = [...root.querySelectorAll('[data-sd-shelf]')];
  const status = root.querySelector('[data-sd-menu-status]');
  const labels = Object.fromEntries(chips.map((c) => [c.dataset.sdFeel || '', c.dataset.sdFeelLabel || '']));

  for (const chip of chips) {
    chip.setAttribute('role', 'button');
    chip.removeAttribute('aria-current');
  }

  const apply = (feel, { announce = true, animate = true } = {}) => {
    if (!(feel in labels)) feel = '';
    let shown = 0;
    for (const li of items) {
      const tags = (li.dataset.tags || '').split(/\s+/);
      const show = !feel || tags.includes(feel);
      const was = !li.hidden;
      li.hidden = !show;
      if (show) {
        shown += 1;
        if (!was && animate && !reduceMotion()) {
          li.classList.remove('is-entering');
          void li.offsetWidth;
          li.classList.add('is-entering');
        }
      }
    }
    for (const shelf of shelves) {
      const n = shelf.querySelectorAll('[data-sd-item]:not([hidden])').length;
      shelf.hidden = n === 0;
      // Sign tile width so sign + cards fill whole rows (3 and 4 column boards).
      const sign = shelf.querySelector('[data-sd-sign]');
      if (sign && n) {
        sign.dataset.s3 = String(3 - (n % 3));
        sign.dataset.s4 = String(4 - (n % 4));
      }
    }
    for (const chip of chips) chip.setAttribute('aria-pressed', String((chip.dataset.sdFeel || '') === feel));
    for (const rail of root.querySelectorAll('[data-sd-shelf-rail]')) rail.scrollLeft = 0;
    if (status && announce) {
      status.textContent = feel
        ? `${shown} ${labels[feel].toLowerCase()} ${shown === 1 ? 'squishy' : 'squishies'} on the counter.`
        : `All ${shown} squishies on the counter.`;
    }
    try {
      const url = new URL(window.location.href);
      if (feel) url.searchParams.set('feel', feel);
      else url.searchParams.delete('feel');
      history.replaceState(history.state, '', url);
    } catch {
      /* ignore */
    }
  };

  // role=button links: Space activates them too.
  root.addEventListener('keydown', (event) => {
    if (event.key !== ' ') return;
    const chip = event.target instanceof Element ? event.target.closest('[data-sd-feel]') : null;
    if (!chip) return;
    event.preventDefault();
    chip.click();
  });

  root.addEventListener('click', (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-sd-feel]') : null;
    if (!chip || !root.contains(chip)) return;
    event.preventDefault();
    const feel = chip.dataset.sdFeel || '';
    const current = chip.getAttribute('aria-pressed') === 'true';
    apply(current ? '' : feel);
  });

  let initial = '';
  try {
    initial = new URL(window.location.href).searchParams.get('feel') || '';
  } catch {
    /* ignore */
  }
  apply(initial, { announce: false, animate: false });
}

/* ------------------------------------------------------------------ */
/* 3. Plate tray                                                        */
/* ------------------------------------------------------------------ */
function initTray(tray) {
  if (tray.dataset.sdlReady) return;
  tray.dataset.sdlReady = 'true';

  const plateIds = new Set((tray.dataset.plateIds || '').split(',').filter(Boolean).map(Number));
  const miniProduct = Number(tray.dataset.miniProductId || 0);
  const size = Number(tray.dataset.size || 8);
  const tiers = (() => {
    try {
      return JSON.parse(tray.dataset.tiers || '[]').sort((a, b) => a.min - b.min);
    } catch {
      return [];
    }
  })();
  const party = tiers[tiers.length - 1] || { min: size, each: 1100, name: 'Party Platter' };
  const money = (cents) => formatMoney(cents);
  // Mustard is reserved for price tags: the platter price sits on a tiny tag inside the pickle tray.
  const tag = (text) => {
    const el = document.createElement('span');
    el.className = 'sd-tray__tag';
    el.textContent = text;
    return el;
  };

  const link = tray.querySelector('[data-sd-tray-link]');
  const title = tray.querySelector('[data-sd-tray-title]');
  const sub = tray.querySelector('[data-sd-tray-sub]');
  const go = tray.querySelector('[data-sd-tray-go]');
  const seats = [...tray.querySelectorAll('[data-sd-seat]')];
  const more = tray.querySelector('[data-sd-tray-more]');
  const mini = tray.querySelector('[data-sd-tray-mini]');
  let count = 0;

  const render = (n, hasMini) => {
    count = n;
    seats.forEach((seat, i) => seat.classList.toggle('is-filled', i < n));
    if (more) {
      more.hidden = n <= size;
      more.textContent = n > size ? `+${n - size}` : '';
    }
    const tier = [...tiers].reverse().find((t) => n >= t.min);
    if (n === 0) {
      const lead = (tray.dataset.lead || 'Mix any # for').replace('#', String(size));
      title.replaceChildren(`${lead} `, tag(money(party.each * size)));
      sub.textContent = `${money(party.each)} each, shipped free`;
      go.textContent = 'Build a plate';
    } else if (n < size) {
      title.textContent = `${n} of ${size} on your platter`;
      sub.textContent = tier
        ? `Now ${money(tier.each)} each · ${size - n} more and it's ${money(party.each)} each`
        : `${size - n} ${size - n === 1 ? 'seat' : 'seats'} left · ${size} for ${money(party.each * size)}, shipped free`;
      go.textContent = 'View plate';
    } else {
      title.replaceChildren(`${party.name}: `, tag(`${money(party.each)} each`));
      const offerOpen = Boolean(mini && mini.dataset.until && todayYmd() <= mini.dataset.until);
      sub.textContent = offerOpen && !hasMini ? 'Your free Christmas Mini is waiting' : 'Shipped free. Nice plate.';
      go.textContent = 'View plate';
      if (mini) mini.hidden = !(offerOpen && !hasMini);
    }
    if (mini && n < size) mini.hidden = true;
    tray.dataset.count = String(n);
    link.setAttribute('aria-label', `${title.textContent}. ${sub.textContent}. ${go.textContent}`);
  };

  const update = async () => {
    try {
      const cart = await getCart();
      const items = cart?.items || [];
      const n = items.reduce((sum, item) => sum + (plateIds.has(Number(item.product_id)) ? item.quantity : 0), 0);
      const hasMini = miniProduct ? items.some((item) => Number(item.product_id) === miniProduct) : false;
      render(n, hasMini);
    } catch {
      /* keep the static default */
    }
  };

  link.addEventListener('click', (event) => {
    if (count > 0) {
      event.preventDefault();
      openCartDrawer();
    }
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const done = event.promise || Promise.resolve();
    done.then(update, update);
  });
  window.addEventListener('pageshow', (event) => event.persisted && update());

  // Only float while its section is on screen, and never over the footer: hide once the end marker shows.
  const host = tray.closest('[data-sd-tray-host]') || tray.parentElement;
  const end = host?.querySelector('[data-sd-tray-end]');
  if (host && 'IntersectionObserver' in window) {
    let hostIn = false;
    let endIn = false;
    const sync = () => tray.classList.toggle('is-visible', hostIn && !endIn);
    new IntersectionObserver(
      ([entry]) => {
        hostIn = entry.isIntersecting;
        sync();
      },
      { rootMargin: '0px 0px -35% 0px' }
    ).observe(host);
    if (end) {
      new IntersectionObserver(([entry]) => {
        endIn = entry.isIntersecting || entry.boundingClientRect.top < 0;
        sync();
      }).observe(end);
    }
  } else {
    tray.classList.add('is-visible');
  }
  update();
}

/* ------------------------------------------------------------------ */
/* 4. Plates landing page                                               */
/* ------------------------------------------------------------------ */
function initPlates(root) {
  if (root.dataset.sdlReady) return;
  root.dataset.sdlReady = 'true';

  let data = {};
  try {
    const host = root.closest('.shopify-section') || document;
    data = JSON.parse(host.querySelector('[data-sdl-plates-data]')?.textContent || '{}');
  } catch {
    data = {};
  }
  const keys = Object.keys(data.plates || {});
  if (!keys.length) return;
  const html = document.documentElement;
  const current = () => (keys.includes(html.dataset.sdPlate) ? html.dataset.sdPlate : data.default);

  const setPlate = (key, { scroll = false } = {}) => {
    if (!keys.includes(key)) return;
    html.dataset.sdPlate = key;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('plate', key);
      history.replaceState(history.state, '', url);
    } catch {
      /* ignore */
    }
    for (const sw of document.querySelectorAll('[data-sdl-plate-link]')) {
      sw.setAttribute('aria-current', String(sw.getAttribute('data-sdl-plate-link') === key));
    }
    watchHeroCta();
    if (scroll) {
      const top = document.getElementById(`sd-plate-buy-${key}`) || root;
      top.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  };

  // Preset switcher links (?plate=key) swap in place; the href still works without JS.
  document.addEventListener('click', (event) => {
    const sw = event.target instanceof Element ? event.target.closest('[data-sdl-plate-link]') : null;
    if (!sw) return;
    event.preventDefault();
    setPlate(sw.getAttribute('data-sdl-plate-link'), { scroll: true });
  });

  // One-tap platter add (hero, sticky bar, final call).
  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-sdl-platter-add]') : null;
    if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    const key = current();
    const plate = data.plates?.[key];
    if (!plate) return;

    const box = document.querySelector(`[data-sdl-preset="${key}"] [data-sdl-mini-opt]`);
    const wantsMini = box instanceof HTMLInputElement && box.checked && !box.closest('[hidden]');
    const items = plate.items.map((item) => ({ id: item.id, quantity: item.quantity, properties: { _plate: plate.name } }));
    if (wantsMini && data.mini) items.push({ id: data.mini, quantity: 1, properties: { _plate: plate.name } });

    const status = document.querySelector(`[data-sdl-preset="${key}"] [data-sdl-add-status]`);
    const label = button.querySelector('[data-sdl-add-label]') || button;
    const original = label.textContent;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    if (status) status.textContent = '';
    try {
      await addItems(items, { source: button });
      squish(button);
      label.textContent = 'Platter is in your bag';
      button.classList.add('is-added');
      setTimeout(() => {
        label.textContent = original;
        button.classList.remove('is-added');
      }, 2200);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (status) status.textContent = message;
      else window.alert(message);
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  });

  // Sticky buy bar: shows once the current hero button has scrolled away.
  const bar = document.querySelector('[data-sdl-sticky]');
  let heroObserver;
  const seen = new Map();
  function watchHeroCta() {
    if (!bar || !('IntersectionObserver' in window)) return;
    heroObserver?.disconnect();
    const cta = document.querySelector(`[data-sdl-preset="${current()}"] [data-sdl-hero-cta]`);
    const end = document.querySelector('[data-sdl-sticky-end]');
    // The sticky header covers the top of the viewport: a hero CTA that is mostly tucked under it cannot really be
    // tapped, so it counts as gone once less than half of it shows below the header (the header's own height, not
    // its distance from the top, which includes the announcement bar).
    const header = document.querySelector('#header-component');
    const cover = header instanceof HTMLElement ? Math.round(header.offsetHeight + (parseFloat(getComputedStyle(header).top) || 0)) : 0;
    heroObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) seen.set(entry.target, entry);
        const ctaEntry = cta ? seen.get(cta) : null;
        const endEntry = end ? seen.get(end) : null;
        const top = (entry) => entry.rootBounds?.top ?? cover;
        const ctaGone = ctaEntry
          ? (!ctaEntry.isIntersecting || ctaEntry.intersectionRatio < 0.5) && ctaEntry.boundingClientRect.top < top(ctaEntry)
          : false;
        const atEnd = endEntry ? endEntry.isIntersecting || endEntry.boundingClientRect.top < top(endEntry) : false;
        bar.classList.toggle('is-visible', ctaGone && !atEnd);
      },
      { rootMargin: `-${cover}px 0px 0px 0px`, threshold: [0, 0.5, 1] }
    );
    if (cta) heroObserver.observe(cta);
    if (end) heroObserver.observe(end);
  }

  if (!keys.includes(html.dataset.sdPlate)) html.dataset.sdPlate = data.default;
  setPlate(current());
}

/* ------------------------------------------------------------------ */
/* 5. In-view loops                                                     */
/* ------------------------------------------------------------------ */
function initVideos(root = document) {
  const videos = [...root.querySelectorAll('video[data-sdl-inview]:not([data-sdl-ready]), [data-sdl-inview] video:not([data-sdl-ready])')];
  if (!videos.length) return;
  for (const video of videos) {
    video.dataset.sdlReady = 'true';
    video.muted = true;
    if (reduceMotion()) {
      video.removeAttribute('autoplay');
      video.pause();
      video.setAttribute('controls', '');
    }
  }
  if (reduceMotion() || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting) {
          if (video.preload === 'none') video.preload = 'auto';
          video.play().catch(() => {});
        } else video.pause();
      }
    },
    { threshold: 0.35 }
  );
  videos.forEach((v) => io.observe(v));
}

/* ------------------------------------------------------------------ */
function boot(root = document) {
  applyGates(root);
  root.querySelectorAll('[data-sd-menu]').forEach(initMenu);
  root.querySelectorAll('[data-sd-tray]').forEach(initTray);
  root.querySelectorAll('[data-sdl-plates]').forEach(initPlates);
  initVideos(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
else boot();
document.addEventListener('shopify:section:load', (event) => boot(/** @type {any} */ (event.target)));
